const BACKGROUNDED_AT_KEY = 'bookaburra:pwa-recovery:backgrounded-at';
const RELOADED_AT_KEY = 'bookaburra:pwa-recovery:reloaded-at';

export const LONG_BACKGROUND_MS = 60 * 60 * 1_000;
export const RELOAD_COOLDOWN_MS = 5 * 60 * 1_000;

interface RecoveryStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface RecoveryOptions {
  storage?: RecoveryStorage;
  now?: () => number;
  reload?: () => void;
  longBackgroundMs?: number;
  reloadCooldownMs?: number;
}

export function isInstalledPwa(
  matchMedia: (query: string) => MediaQueryList = window.matchMedia.bind(window),
  navigatorValue: Navigator = navigator,
): boolean {
  const iosNavigator = navigatorValue as Navigator & { standalone?: boolean };
  return matchMedia('(display-mode: standalone)').matches || iosNavigator.standalone === true;
}

export function isDeploymentAssetError(value: unknown): boolean {
  const message =
    value instanceof Error
      ? value.message
      : typeof value === 'string'
        ? value
        : value && typeof value === 'object' && 'message' in value
          ? String(value.message)
          : '';

  return /(?:failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|chunkloaderror|loading chunk [^ ]+ failed)/i.test(
    message,
  );
}

export function isHashedAssetUrl(value: string, origin: string): boolean {
  try {
    const url = new URL(value, origin);
    return (
      url.origin === origin &&
      /^\/assets\/.+-[A-Za-z0-9_-]{8,}\.(?:js|css)$/.test(url.pathname)
    );
  } catch {
    return false;
  }
}

export function createPwaRecovery(options: RecoveryOptions = {}) {
  const storage = options.storage ?? window.sessionStorage;
  const now = options.now ?? Date.now;
  const reload = options.reload ?? (() => window.location.reload());
  const longBackgroundMs = options.longBackgroundMs ?? LONG_BACKGROUND_MS;
  const reloadCooldownMs = options.reloadCooldownMs ?? RELOAD_COOLDOWN_MS;

  function recordBackgrounded(): void {
    writeTimestamp(storage, BACKGROUNDED_AT_KEY, now());
  }

  function recoverAfterResume(): boolean {
    const backgroundedAt = readTimestamp(storage, BACKGROUNDED_AT_KEY);
    removeItem(storage, BACKGROUNDED_AT_KEY);
    if (backgroundedAt === null || now() - backgroundedAt < longBackgroundMs) {
      return false;
    }
    return reloadOnce();
  }

  function reloadOnce(): boolean {
    const currentTime = now();
    const lastReloadAt = readTimestamp(storage, RELOADED_AT_KEY);
    if (lastReloadAt !== null && currentTime - lastReloadAt < reloadCooldownMs) {
      return false;
    }
    if (!writeTimestamp(storage, RELOADED_AT_KEY, currentTime)) return false;
    reload();
    return true;
  }

  return { recordBackgrounded, recoverAfterResume, reloadOnce };
}

export function installPwaRecovery(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const recovery = createPwaRecovery();
  const standalone = isInstalledPwa();

  if (standalone) {
    const recordIfHidden = () => {
      if (document.visibilityState === 'hidden') recovery.recordBackgrounded();
    };
    const recoverIfVisible = () => {
      if (document.visibilityState === 'visible') recovery.recoverAfterResume();
    };

    document.addEventListener('visibilitychange', () => {
      recordIfHidden();
      recoverIfVisible();
    });
    window.addEventListener('pagehide', recovery.recordBackgrounded);
    window.addEventListener('pageshow', recoverIfVisible);
    document.addEventListener('freeze', recovery.recordBackgrounded);
    document.addEventListener('resume', recoverIfVisible);
  }

  window.addEventListener('vite:preloadError', (event) => {
    if (recovery.reloadOnce()) event.preventDefault();
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (isDeploymentAssetError(event.reason)) recovery.reloadOnce();
  });

  window.addEventListener(
    'error',
    (event) => {
      const target = event.target as (EventTarget & { src?: string; href?: string }) | null;
      const assetUrl = target?.src ?? target?.href;
      if (assetUrl && isHashedAssetUrl(assetUrl, window.location.origin)) {
        recovery.reloadOnce();
      }
    },
    true,
  );
}

function readTimestamp(storage: RecoveryStorage, key: string): number | null {
  try {
    const value = storage.getItem(key);
    if (value === null) return null;
    const timestamp = Number(value);
    return Number.isFinite(timestamp) ? timestamp : null;
  } catch {
    return null;
  }
}

function writeTimestamp(storage: RecoveryStorage, key: string, value: number): boolean {
  try {
    storage.setItem(key, String(value));
    return true;
  } catch {
    return false;
  }
}

function removeItem(storage: RecoveryStorage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Recovery is best-effort when storage is unavailable.
  }
}
